import { useCallback, useEffect, useState } from "react";
import { api, type StreamHealthOverrideStatus } from "../api/client";

/**
 * Dérogation ponctuelle du plafond spread de CIRCUIT_STREAM_HEALTH (owner 2026-09-02).
 * Calqué sur useAutoMode : poll GET /status, actions POST/DELETE sans OTP (le geste est
 * délibéré, la prochaine ouverture repasse toutes les autres gates normalement). Se
 * consomme seule côté backend à la prochaine ouverture bot réussie.
 */

export interface StreamHealthOverrideState {
  status: StreamHealthOverrideStatus | null;
  pending: boolean;
  activate: () => Promise<StreamHealthOverrideStatus>;
  cancel: () => Promise<StreamHealthOverrideStatus>;
}

export function useStreamHealthOverride(pollIntervalMs = 5_000): StreamHealthOverrideState {
  const [status, setStatus] = useState<StreamHealthOverrideStatus | null>(null);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.streamHealthOverrideStatus();
        if (!cancelled) setStatus(s);
      } catch {
        // Silencieux — la bannière system-state porte l'injoignabilité backend.
      }
    };
    void tick();
    const id = setInterval(tick, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollIntervalMs]);

  const activate = useCallback(async (): Promise<StreamHealthOverrideStatus> => {
    setPending(true);
    try {
      const s = await api.activateStreamHealthOverride();
      setStatus(s);
      return s;
    } finally {
      setPending(false);
    }
  }, []);

  const cancel = useCallback(async (): Promise<StreamHealthOverrideStatus> => {
    setPending(true);
    try {
      const s = await api.cancelStreamHealthOverride();
      setStatus(s);
      return s;
    } finally {
      setPending(false);
    }
  }, []);

  return { status, pending, activate, cancel };
}
